import { AlertTriangle, Copy } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

interface ShowServiceAccountSecretDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accessKeyId: string;
  secretKey: string;
  title?: string;
  description?: string;
}

export function ShowServiceAccountSecretDialog({
  open,
  onOpenChange,
  accessKeyId,
  secretKey,
  title = 'Save your secret key',
  description = 'This secret will not be shown again. Store it in a secure secret manager now.',
}: ShowServiceAccountSecretDialogProps) {
  const [acknowledged, setAcknowledged] = useState(false);

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied to clipboard`);
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      setAcknowledged(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-attention-muted text-attention-foreground border border-attention-muted/50 px-4 py-3 rounded-md text-sm">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">Shown once</p>
                <p className="mt-1">
                  We never store the plaintext secret. If you lose it, you must rotate to issue a
                  new one.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Access Key ID</Label>
            <div
              className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2 cursor-pointer hover:bg-muted/80"
              role="button"
              tabIndex={0}
              onClick={() => handleCopy(accessKeyId, 'Access key ID')}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') handleCopy(accessKeyId, 'Access key ID');
              }}
            >
              <code className="flex-1 text-sm font-mono break-all">{accessKeyId}</code>
              <Copy className="h-4 w-4 shrink-0 text-muted-foreground" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Secret Key</Label>
            <div
              className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2 cursor-pointer hover:bg-muted/80"
              role="button"
              tabIndex={0}
              onClick={() => handleCopy(secretKey, 'Secret key')}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') handleCopy(secretKey, 'Secret key');
              }}
            >
              <code className="flex-1 text-sm font-mono break-all">{secretKey}</code>
              <Copy className="h-4 w-4 shrink-0 text-muted-foreground" />
            </div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer select-none pt-2">
            <Checkbox
              checked={acknowledged}
              onCheckedChange={value => setAcknowledged(value === true)}
              className="mt-0.5"
            />
            <span className="text-sm text-muted-foreground">
              I have copied and securely stored the secret key. I understand it will not be shown
              again.
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button onClick={() => handleClose(false)} disabled={!acknowledged}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
